import { useEffect, useRef } from 'react';

import { mapBridge, MAP_MAX_PITCH, MAP_TOP_DOWN_PITCH } from './mapBridge';
import {
  buildCelestialStarfield,
  paintCelestialStarfield,
  skyHorizonMask,
  skyPitchBlend,
} from './starfield';

const STARS = buildCelestialStarfield(420);

export function StarrySky() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const draw = () => {
      const pitch = mapBridge.getPitch();
      const bearing = mapBridge.getBearing();
      const pitchT = skyPitchBlend(pitch, MAP_MAX_PITCH, MAP_TOP_DOWN_PITCH);

      wrap.style.opacity = String(pitchT * 0.98);
      const mask = skyHorizonMask(pitchT);
      wrap.style.maskImage = mask;
      wrap.style.webkitMaskImage = mask;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintCelestialStarfield(ctx, w, h, STARS, pitch, bearing, pitchT);
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    const unsubP = mapBridge.subscribePitch(draw);
    const unsubB = mapBridge.subscribeBearing(draw);
    return () => {
      ro.disconnect();
      unsubP();
      unsubB();
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute inset-0 z-[1] overflow-hidden opacity-0"
      aria-hidden
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
}
